import type {
    ICaseDataListResponse,
    ICaseDataResponse,
    ICommitUpload,
    IInitiateSessionDedupHitResponse,
    IInitiateUploadSession,
    IUploadChunkResponse,
    IUploadCommitResponse,
    IUploadSessionResponse,
    IUploadSessionStatus,
} from '@escloud/types';

import type { ApiClient } from './api-client';
import { getByKey, setByKey, touch } from './cache-middleware';
import { calculateSHA256 } from '../lib/file-utils';
import type { IDataService } from '../visualization/services';

/**
 * Return shape of `initiateUpload`: either a fresh session or, when
 * the server detected a dedup hit on the supplied contentSha256, an
 * early "skip the upload" response.
 */
export type InitiateUploadResult = IUploadSessionResponse | IInitiateSessionDedupHitResponse;

export function isDedupHit(result: InitiateUploadResult): result is IInitiateSessionDedupHitResponse {
    return 'dedupHit' in result && result.dedupHit === true;
}

export class DataService implements IDataService {
    constructor(private api: ApiClient) {}

    initiateUpload(data: IInitiateUploadSession, signal?: AbortSignal): Promise<InitiateUploadResult> {
        return this.api.post<InitiateUploadResult>(`/datasets/upload-sessions`, data, { signal });
    }

    uploadChunk(
        uploadId: string,
        chunkIndex: number,
        data: ArrayBuffer,
        signal?: AbortSignal
    ): Promise<IUploadChunkResponse> {
        return this.api.putBinary<IUploadChunkResponse>(
            `/datasets/upload-sessions/${uploadId}/chunks/${chunkIndex}`,
            data,
            { signal }
        );
    }

    /**
     * Commit an upload session.
     *
     * The request is always multipart: commit metadata travels as a JSON
     * `metadata` form field, and the optional proxy-preview MHA travels as
     * a `preview` file part. Returns the new InputDataset id; binding to a
     * case is the caller's responsibility (atomic `POST /cases { inputs }`
     * or `attachDataset` rebind).
     */
    commitUpload(
        uploadId: string,
        data: ICommitUpload,
        previewBuffer?: ArrayBuffer,
        signal?: AbortSignal
    ): Promise<IUploadCommitResponse> {
        const formData = new FormData();
        formData.append('metadata', JSON.stringify(data));
        if (previewBuffer) {
            formData.append(
                'preview',
                new Blob([previewBuffer], { type: 'application/octet-stream' }),
                'preview.mha'
            );
        }
        return this.api.postMultipart<IUploadCommitResponse>(
            `/datasets/upload-sessions/${uploadId}/commit`,
            formData,
            { signal }
        );
    }

    getUploadStatus(uploadId: string): Promise<IUploadSessionStatus> {
        return this.api.get<IUploadSessionStatus>(`/datasets/upload-sessions/${uploadId}`);
    }

    abortUpload(uploadId: string): Promise<void> {
        return this.api.delete<void>(`/datasets/upload-sessions/${uploadId}`);
    }

    /**
     * JSON-mode attach: bind an existing InputDataset (gateway-pushed or
     * previously uploaded) to a workflow slot — no bytes move. Resulting
     * `CaseData.source` is `'gateway_pick'`. Server returns 409
     * `dataset_unavailable` when the sweeper raced; callers should
     * surface this so the user can re-pick.
     */
    attachDataset(caseId: string, key: string, inputDatasetId: string): Promise<ICaseDataResponse> {
        return this.api.post<ICaseDataResponse>(`/cases/${caseId}/data`, { inputDatasetId, key });
    }

    // ========================================================================
    // Visualization Data Methods (IVisualizationDataService)
    // ========================================================================

    /**
     * List data items for a case, optionally filtered by key (slot name).
     */
    async listData(caseId: string, key?: string, signal?: AbortSignal): Promise<ICaseDataResponse[]> {
        const queryParams = key ? `?key=${encodeURIComponent(key)}` : '';
        const response = await this.api.get<ICaseDataListResponse>(`/cases/${caseId}/data${queryParams}`, {
            signal,
        });
        return response.items;
    }

    /**
     * Get metadata about a specific data item by ID.
     */
    async getDataById(caseId: string, dataId: string): Promise<ICaseDataResponse> {
        return this.api.get<ICaseDataResponse>(`/cases/${caseId}/data/${dataId}`);
    }

    /**
     * Download data content as ArrayBuffer.
     */
    async downloadData(
        caseId: string,
        dataId: string,
        hashSum: string,
        signal?: AbortSignal
    ): Promise<ArrayBuffer> {
        const cacheKey = `${caseId}-${dataId}`;
        const expectedHash = hashSum?.trim() ?? '';

        const cached = await getByKey<ArrayBuffer>(cacheKey);
        if (cached) {
            touch(cacheKey).catch(() => {});
            return cached.value;
        }

        const buffer = await this.api.getBinary(`/cases/${caseId}/data/${dataId}/download`, { signal });
        if (!buffer?.byteLength) {
            throw new Error(`Downloaded empty file for data ${dataId}`);
        }

        if (expectedHash) {
            const sha256 = await calculateSHA256(buffer);
            if (sha256 !== expectedHash) {
                throw new Error(`Invalid file hash: expected ${expectedHash}, got ${sha256}`);
            }
        }

        setByKey(cacheKey, buffer.slice(0)).catch(() => {});
        return buffer;
    }

    /**
     * Delete a data item by ID.
     */
    deleteData(caseId: string, dataId: string): Promise<void> {
        return this.api.delete<void>(`/cases/${caseId}/data/${dataId}`);
    }

    /**
     * Delete all data items matching a key.
     */
    async deleteDataByKey(caseId: string, key: string): Promise<void> {
        const items = await this.listData(caseId, key);
        await Promise.all(items.map((item) => this.deleteData(caseId, item.id)));
    }

    /**
     * Convenience method: find data by key and download content.
     */
    async getDataContentByKey(caseId: string, key: string): Promise<ArrayBuffer> {
        const items = await this.listData(caseId, key);
        if (items.length === 0) {
            throw new Error(`No data found for key "${key}" in case ${caseId}`);
        }
        return this.downloadData(caseId, items[0].id, items[0].file?.sha256 ?? '');
    }

    /**
     * Convenience method: find JSON data by key, download and parse.
     */
    async getJsonDataByKey<T = unknown>(caseId: string, key: string): Promise<T> {
        const buffer = await this.getDataContentByKey(caseId, key);
        const text = new TextDecoder().decode(buffer);
        return JSON.parse(text) as T;
    }
}
